import React from "react";

const ToggleRow = ({ label, description, value, onChange }) => (
  <label className="user-hub__toggle">
    <input
      type="checkbox"
      checked={Boolean(value)}
      onChange={(event) => onChange(event.target.checked)}
    />
    <div>
      <div className="user-hub__toggle-label">{label}</div>
      <div className="user-hub__toggle-description">{description}</div>
    </div>
  </label>
);

const formatDate = (dateValue) => {
  if (!dateValue) return "-";
  try {
    return new Date(dateValue).toLocaleDateString(undefined, {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  } catch (error) {
    return "-";
  }
};

export default function CommunityControlCenter({
  user,
  isPro,
  editionName,
  badges = [],
  tagline,
  description,
  settings = {},
  onSettingChange,
  onSignIn,
  onRegister,
  onLogout,
  onRequestUpgrade,
  stats = {},
  config = {},
  hideUpgrade,
}) {
  const {
    showCommunityToggles = true,
    showTeamCTA = false,
    allowAnonymousPublishing = true,
  } = config || {};
  const [showDeleteConfirm, setShowDeleteConfirm] = React.useState(false);
  const photoInputRef = React.useRef(null);

  const safeSettings = {
    displayName: "",
    handle: "",
    website: "",
    bio: "",
    shopLinks: [],
    shareGallery: true,
    allowMessages: true,
    allowDownloads: false,
    showWorkInProgress: true,
    autoPublish: false,
    allowCollabInvites: true,
    allowBetaAccess: false,
    shareAnalytics: false,
    profilePhoto: "",
    memberSince: "",
    deleteRequestedAt: null,
    deleteScheduledFor: null,
    ...settings,
  };

  const designCount = Number.isFinite(stats.designCount)
    ? stats.designCount
    : 0;
  const memberSinceLabel = formatDate(safeSettings.memberSince);
  const memberSinceDate = safeSettings.memberSince
    ? new Date(safeSettings.memberSince)
    : null;
  const daysActive = memberSinceDate
    ? Math.max(
        1,
        Math.floor(
          (Date.now() - memberSinceDate.getTime()) / (1000 * 60 * 60 * 24)
        )
      )
    : 0;
  const timeUsingLabel = daysActive
    ? `${daysActive} day${daysActive === 1 ? "" : "s"}`
    : "-";
  const profilePhoto = safeSettings.profilePhoto || user?.photoURL || "";
  const displayName =
    safeSettings.displayName || user?.displayName || "Unnamed stitcher";
  const handleLabel = safeSettings.handle ? `@${safeSettings.handle}` : "";
  const websiteLabel = safeSettings.website?.trim() || "";
  const shopLinks = Array.isArray(safeSettings.shopLinks)
    ? safeSettings.shopLinks
    : [];
  const deletionDate = safeSettings.deleteScheduledFor
    ? new Date(safeSettings.deleteScheduledFor)
    : null;
  const daysUntilDeletion = deletionDate
    ? Math.max(
        0,
        Math.ceil(
          (deletionDate.getTime() - Date.now()) / (1000 * 60 * 60 * 24)
        )
      )
    : null;

  const profileFields = [
    {
      key: "displayName",
      label: "Display name",
      placeholder: "Stitch alias",
    },
    {
      key: "handle",
      label: "Username",
      placeholder: "stitcher",
      prefix: "@",
    },
  ];
  const extraFields = [
    {
      key: "website",
      label: "Portfolio / link (optional)",
      placeholder: "https://example.com",
    },
  ];

  const toggleFields = [
    {
      key: "shareGallery",
      label: "Share finished pieces",
      description: "Publish a public gallery when community launches.",
    },
    {
      key: "showWorkInProgress",
      label: "Show works in progress",
      description: "Let others peek at drafts and time-lapses.",
    },
    {
      key: "allowMessages",
      label: "Allow private messages",
      description: "Enable DMs and feedback requests.",
    },
    {
      key: "allowDownloads",
      label: "Allow design downloads",
      description: "Let the community export your charts.",
    },
    {
      key: "allowCollabInvites",
      label: "Collaborator invites",
      description: "Opt-in to co-create with other stitchers.",
    },
    {
      key: "autoPublish",
      label: "Auto-publish new uploads",
      description: "Skip the manual review step when sharing.",
    },
    {
      key: "allowBetaAccess",
      label: "Beta + feature experiments",
      description: "Get early access to the community hub.",
    },
    {
      key: "shareAnalytics",
      label: "Share usage analytics",
      description: "Send anonymous stats to help tune the roadmap.",
    },
  ];

  const statsList = [
    {
      label: "Time using app",
      value: timeUsingLabel,
    },
    {
      label: "Designs saved",
      value: designCount.toLocaleString(),
    },
    {
      label: "Layers in this design",
      value: stats.layerCount ?? "0",
    },
    {
      label: "Palette colors selected",
      value: stats.paletteCount ?? "0",
    },
    {
      label: "Total stitches",
      value: stats.totalStitches ?? "0",
    },
    {
      label: "Estimated hours",
      value: stats.estimatedHours ? `${stats.estimatedHours}h` : "0h",
    },
    {
      label: stats.sizeLabel || "Fabric size",
      value: stats.sizeValue ?? stats.fabricSize ?? "-",
    },
    {
      label: "Fractional ready",
      value: stats.fractionalEnabled ? "Enabled" : "Off",
    },
  ];

  const handleTextChange = (key) => (event) => {
    onSettingChange?.(key, event.target.value);
  };

  const handleToggleChange = (key) => (value) => {
    onSettingChange?.(key, value);
  };

  const handlePhotoClick = () => {
    if (photoInputRef.current) {
      photoInputRef.current.click();
    }
  };

  const handlePhotoChange = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      onSettingChange?.("profilePhoto", reader.result || "");
    };
    reader.readAsDataURL(file);
    event.target.value = null;
  };

  const handleConfirmDeletion = () => {
    const scheduledFor = new Date(Date.now() + 30 * 24 * 60 * 60 * 1000);
    onSettingChange?.("deleteRequestedAt", new Date().toISOString());
    onSettingChange?.("deleteScheduledFor", scheduledFor.toISOString());
    setShowDeleteConfirm(false);
  };

  const handleCancelDeletion = () => {
    onSettingChange?.("deleteRequestedAt", null);
    onSettingChange?.("deleteScheduledFor", null);
    setShowDeleteConfirm(false);
  };

  const handleShopLinkChange = (index, field) => (event) => {
    const next = [...shopLinks];
    const current = next[index] || { label: "", url: "" };
    next[index] = { ...current, [field]: event.target.value };
    onSettingChange?.("shopLinks", next);
  };

  const handleAddShopLink = () => {
    onSettingChange?.("shopLinks", [...shopLinks, { label: "", url: "" }]);
  };

  const handleRemoveShopLink = (index) => {
    const next = shopLinks.filter((_, idx) => idx !== index);
    onSettingChange?.("shopLinks", next);
  };

  return (
    <section className="user-hub">
      <div className="user-hub__cards">
        <div className="user-hub__card">
          <div className="user-hub__card-header">
            <h3>Account access</h3>
            {isPro && (
              <span className="user-hub__pro-pill">All features unlocked</span>
            )}
          </div>
          {user ? (
            <div className="user-hub__account">
              <p className="user-hub__account-name">
                {user.displayName || "Unnamed stitcher"}
              </p>
              <p className="user-hub__account-email">
                {user.email || "Google account linked"}
              </p>
              <div className="user-hub__account-actions">
                <button className="btn" type="button" onClick={onLogout}>
                  Sign out
                </button>
                {!isPro && !hideUpgrade && (
                  <button
                    className="btn btn-good"
                    type="button"
                    onClick={onRequestUpgrade}
                  >
                    Unlock Pro
                  </button>
                )}
              </div>
            </div>
          ) : (
            <div className="user-hub__account user-hub__account--empty">
              <p>
                Reserve your community handle, sync favorites, and follow other
                designers once the hub launches.
              </p>
              <div className="user-hub__account-actions">
                <button
                  className="btn btn-primary"
                  type="button"
                  onClick={onRegister}
                >
                  Create account
                </button>
                <button className="btn" type="button" onClick={onSignIn}>
                  Sign in with Google
                </button>
              </div>
            </div>
          )}
        </div>

        <div className="user-hub__card">
          <div className="user-hub__card-header">
            <h3>Profile setup</h3>
            <span className="user-hub__muted">
              Set your public username and avatar.
            </span>
          </div>
          <div className="user-hub__profile-row">
            <div className="user-hub__avatar">
              {profilePhoto ? (
                <img src={profilePhoto} alt="Profile" />
              ) : (
                <div className="user-hub__avatar-placeholder">
                  Add a profile photo
                </div>
              )}
            </div>
            <div className="user-hub__avatar-actions">
              <input
                ref={photoInputRef}
                type="file"
                accept="image/*"
                onChange={handlePhotoChange}
                style={{ display: "none" }}
              />
            <div className="user-hub__avatar-buttons">
              <button className="btn" type="button" onClick={handlePhotoClick}>
                Set profile photo
              </button>
                {profilePhoto && (
                  <button
                    className="btn"
                    type="button"
                    onClick={() => onSettingChange?.("profilePhoto", "")}
                  >
                    Remove
                  </button>
                )}
              </div>
              <span className="user-hub__muted">
                Custom photos are stored on this device for now.
              </span>
            </div>
          </div>
          <div className="user-hub__inputs">
            {profileFields.map((field) => (
              <label key={field.key}>
                <span className="user-hub__label">{field.label}</span>
                <div
                  className={`user-hub__input-wrapper${
                    field.prefix ? " has-prefix" : ""
                  }`}
                >
                  {field.prefix && (
                    <span className="user-hub__input-prefix">
                      {field.prefix}
                    </span>
                  )}
                  <input
                    type="text"
                    className="input"
                    placeholder={field.placeholder}
                    value={safeSettings[field.key] ?? ""}
                    onChange={handleTextChange(field.key)}
                  />
                </div>
              </label>
            ))}
          </div>
        </div>

        <div className="user-hub__card">
          <div className="user-hub__card-header">
            <h3>Profile details</h3>
            <span className="user-hub__muted">
              Synced to your account when signed in.
            </span>
          </div>
          <div className="user-hub__inputs">
            <label>
              <span className="user-hub__label">Registered email</span>
              <input
                type="text"
                className="input user-hub__readonly"
                value={user?.email || "Not signed in"}
                readOnly
              />
            </label>
            <label>
              <span className="user-hub__label">Member since</span>
              <input
                type="text"
                className="input user-hub__readonly"
                value={memberSinceLabel}
                readOnly
              />
            </label>
            {extraFields.map((field) => (
              <label key={field.key}>
                <span className="user-hub__label">{field.label}</span>
                <input
                  type="text"
                  className="input"
                  placeholder={field.placeholder}
                  value={safeSettings[field.key] ?? ""}
                  onChange={handleTextChange(field.key)}
                />
              </label>
            ))}
            <div>
              <span className="user-hub__label">Shop links</span>
              <div className="user-hub__link-list">
                {shopLinks.length === 0 && (
                  <span className="user-hub__muted">
                    Add links to Etsy, Shopify, Gumroad, or your store.
                  </span>
                )}
                {shopLinks.map((link, index) => (
                  <div key={`shop-link-${index}`} className="user-hub__link-row">
                    <input
                      type="text"
                      className="input"
                      placeholder="Shop name (optional)"
                      value={link?.label || ""}
                      onChange={handleShopLinkChange(index, "label")}
                    />
                    <input
                      type="url"
                      className="input"
                      placeholder="https://"
                      value={link?.url || ""}
                      onChange={handleShopLinkChange(index, "url")}
                    />
                    <button
                      className="btn"
                      type="button"
                      onClick={() => handleRemoveShopLink(index)}
                    >
                      Remove
                    </button>
                  </div>
                ))}
              </div>
              <button className="btn" type="button" onClick={handleAddShopLink}>
                Add another link
              </button>
            </div>
            <label>
              <span className="user-hub__label">Bio</span>
              <textarea
                className="input"
                rows={3}
                placeholder="Share your favorite techniques, preferred fabric counts, or current goals."
                value={safeSettings.bio ?? ""}
                onChange={handleTextChange("bio")}
              />
            </label>
          </div>
        </div>

        <div className="user-hub__card">
          <div className="user-hub__card-header">
            <h3>Profile preview</h3>
            <span className="user-hub__muted">
              This is how your community profile will appear.
            </span>
          </div>
          <div className="user-hub__preview">
            <div className="user-hub__avatar user-hub__avatar--small">
              {profilePhoto ? (
                <img src={profilePhoto} alt="Profile" />
              ) : (
                <div className="user-hub__avatar-placeholder">No photo</div>
              )}
            </div>
            <div className="user-hub__preview-details">
              <div className="user-hub__preview-name">{displayName}</div>
              {handleLabel && (
                <div className="user-hub__preview-handle">{handleLabel}</div>
              )}
              {safeSettings.bio && (
                <p className="user-hub__preview-bio">{safeSettings.bio}</p>
              )}
              {(websiteLabel || shopLinks.length > 0) && (
                <div className="user-hub__preview-links">
                  {websiteLabel && (
                    <span className="user-hub__preview-link">
                      {websiteLabel}
                    </span>
                  )}
                  {shopLinks
                    .filter((link) => link?.url)
                    .map((link, index) => (
                      <span
                        key={`preview-shop-${index}`}
                        className="user-hub__preview-link"
                      >
                        {link.label ? `${link.label}: ` : ""}
                        {link.url}
                      </span>
                    ))}
                </div>
              )}
            </div>
          </div>
        </div>

        {showCommunityToggles && (
          <div className="user-hub__card">
            <div className="user-hub__card-header">
              <h3>Community sharing</h3>
              <span className="user-hub__muted">
                Fine-tune what others can do with your profile.
              </span>
            </div>
            <div className="user-hub__toggle-list">
              {toggleFields.map((toggle) => (
                <ToggleRow
                  key={toggle.key}
                  label={toggle.label}
                  description={toggle.description}
                  value={safeSettings[toggle.key]}
                  onChange={handleToggleChange(toggle.key)}
                />
              ))}
            </div>
            {showTeamCTA && (
              <div className="user-hub__cta">
                <strong>Community builders wanted</strong>
                <p>
                  We&apos;re recruiting founding members to seed the idea
                  gallery. Opt-in to beta access and we&apos;ll reach out.
                </p>
                <button className="btn" type="button" onClick={onSignIn}>
                  I&apos;m interested
                </button>
              </div>
            )}
          </div>
        )}

        <div className="user-hub__card user-hub__card--full">
          <div className="user-hub__card-header">
            <h3>Usage stats</h3>
            <span className="user-hub__muted">
              Snapshot of your stitching activity.
            </span>
          </div>
          <div className="user-hub__stat-grid">
            {statsList.map((stat) => (
              <div key={stat.label} className="user-hub__stat">
                <div className="user-hub__stat-label">{stat.label}</div>
                <div className="user-hub__stat-value">{stat.value}</div>
              </div>
            ))}
          </div>
        </div>

        <div className="user-hub__card">
          <div className="user-hub__card-header">
            <h3>Data deletion</h3>
            <span className="user-hub__muted">
              Request account deletion or cancel a scheduled removal.
            </span>
          </div>
          {deletionDate ? (
            <div className="user-hub__danger-box">
              <strong>Deletion scheduled</strong>
              <p>
                Your account is set to be deleted on{" "}
                <strong>{formatDate(deletionDate)}</strong>.
                {daysUntilDeletion !== null && (
                  <span> ({daysUntilDeletion} days remaining)</span>
                )}
              </p>
              <button
                className="btn"
                type="button"
                onClick={handleCancelDeletion}
              >
                Cancel deletion request
              </button>
            </div>
          ) : (
            <>
              <button
                className="btn danger"
                type="button"
                disabled={!user}
                title={!user ? "Sign in to manage account deletion." : ""}
                onClick={() => setShowDeleteConfirm(true)}
              >
                Delete account
              </button>
              {showDeleteConfirm && (
                <div className="user-hub__danger-box">
                  <strong>Confirm account deletion</strong>
                  <p>
                    This schedules deletion for 30 days from today. You can
                    cancel anytime before then.
                  </p>
                  <div className="user-hub__danger-actions">
                    <button
                      className="btn danger"
                      type="button"
                      onClick={handleConfirmDeletion}
                    >
                      Confirm 30-day deletion
                    </button>
                    <button
                      className="btn"
                      type="button"
                      onClick={() => setShowDeleteConfirm(false)}
                    >
                      Keep account
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </section>
  );
}
